/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import React from 'react';
import Typography from '@material-ui/core/Typography';
import makeStyles from '@material-ui/styles/makeStyles';

import { Accordion, AccordionSummary, AccordionDetails } from '@material-ui/core';
import ExpandIcon from '@material-ui/icons/ExpandMore';

import { useTranslation } from 'react-i18next';
import Tooltip from '../../common/Tooltip';
import FeeTable from './FeeTable';
import { uiSchemaProperties } from '../../../shared/enums/uiSchemaProperties';

const useStyles = makeStyles(theme => ({
  flexDiv: {
    background: 'white',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    paddingBottom: '1.2rem',
  },
  accordion: {
    marginTop: '1.2rem',
  },
  accordionDetails: {
    background: theme.palette.secondary.main,
  },
  alignCenter: {
    alignItems: 'center',
    '&.Mui-expanded': {
      marginTop: '0.6rem',
      marginBottom: 0,
      minHeight: '2rem',
    },
  },
  accordionSummary: {
    display: 'flex',
    alignItems: 'center',
    background: theme.palette.secondary.main,
    fontStyle: 'normal',
    fontWeight: 'bold',
    color: theme.palette.accordionText,
  },
  tooltipIcon: {
    color: theme.palette.primary.dark,
  },
  feeCategory: {
    fontWeight: 'bold',
    fontSize: '1rem',
  },
}));

export function FeeCategories({ uiSchema, formData, disabled, onChange, onBlur, formContext, registry }) {
  const classes = useStyles();
  const { t } = useTranslation();
  const feesSelection = uiSchema[uiSchemaProperties.UI_DATA];

  let total = 0;
  if (feesSelection?.category) {
    total = formData?.selectedFees?.find(fee => t(fee.category) === feesSelection?.category) ? formData.total : 0;
  } else {
    total = formData?.selectedFees?.length ? formData.total : 0;
  }

  if (!Array.isArray(feesSelection)) {
    return (
      <FeeTable
        uiSchema={uiSchema}
        formData={formData}
        disabled={disabled}
        onChange={onChange}
        onBlur={onBlur}
        formContext={formContext}
        total={total}
        registry={registry}
      />
    );
  }

  return (
    <>
      {feesSelection?.map((feeSelection) => {
        const id = `fees-${feeSelection.category?.replace(/ /g, '-')}`;
        const inputProps = { defaultExpanded: feeSelection['ui:open'], className: classes.accordion };
        if (feeSelection['ui:expandEvent'] !== undefined) {
          inputProps.expanded = !!feeSelection['ui:expandEvent'];
        }
        return (
          <div data-testid={feeSelection.category} key={id}>
            <Accordion {...inputProps}>
              <AccordionSummary
                className={classes.accordionSummary}
                classes={{ content: classes.alignCenter, expanded: classes.alignCenter }}
                expandIcon={<ExpandIcon />}
              >
                <Typography
                  variant="h6"
                  component="span"
                  className={classes.feeCategory}
                  style={feeSelection['ui:optional']?.style}
                >
                  {feeSelection.category}
                </Typography>
                {feeSelection.tooltip && (
                  <Tooltip title={feeSelection.tooltip} className={classes.tooltipIcon} arrow placement="right" />
                )}
              </AccordionSummary>
              <AccordionDetails className={classes.accordionDetails}>
                <FeeTable
                  uiSchema={uiSchema}
                  feesSelection={feeSelection}
                  formData={formData}
                  disabled={disabled}
                  onChange={onChange}
                  onBlur={onBlur}
                  formContext={formContext}
                  total={total}
                  registry={registry}
                />
              </AccordionDetails>
            </Accordion>
          </div>
        );
      })}
    </>
  );
}
